import React, { useState } from 'react';
import {
  Card,
  Image,
  Text,
  Badge,
  Group,
  ActionIcon,
  SimpleGrid,
  ScrollArea,
  Modal,
  TextInput,
  Stack,
  Menu,
  Box,
  AspectRatio,
  Button,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCopy,
  IconDots,
  IconPlus,
} from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';

interface GalleryViewProps {
  database: Database;
  items: DatabaseItem[];
  coverPropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}

const COLOR_PALETTE = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#52B788',
];

export const GalleryView: React.FC<GalleryViewProps> = ({
  database,
  items,
  coverPropertyId,
  onItemSelect,
  onItemCreate,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);

  const getItemColor = (itemId: string): string => {
    const hash = itemId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return COLOR_PALETTE[hash % COLOR_PALETTE.length];
  };

  const handleCreateItem = () => {
    if (!newItemTitle.trim()) return;
    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: database.id,
      title: newItemTitle,
      properties: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onItemCreate?.(newItem);
    setIsAddingItem(false);
    setNewItemTitle('');
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  return (
    <div className="gallery-view-wrapper" style={{ width: '100%' }}>
      <ScrollArea style={{ height: 'calc(100vh - 200px)' }}>
        <div style={{ padding: '20px' }}>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing="lg">
            <Card
              p="lg"
              radius="md"
              withBorder
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                cursor: 'pointer',
                backgroundColor: '#f8f9fa',
                borderStyle: 'dashed',
              }}
              onClick={() => setIsAddingItem(true)}
            >
              <Stack align="center" gap="md">
                <IconPlus size={32} />
                <Text c="dimmed" size="sm" fw={500}>
                  Add item
                </Text>
              </Stack>
            </Card>

            {items.map(item => (
              <Card
                key={item.id}
                p={0}
                radius="md"
                withBorder
                overflow="hidden"
                shadow="sm"
              >
                <AspectRatio ratio={16 / 10} bg={getItemColor(item.id)}>
                  <Box />
                </AspectRatio>

                <div style={{ padding: '12px' }}>
                  <Group justify="space-between" align="flex-start" mb="xs">
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Text
                        size="sm"
                        fw={600}
                        lineClamp={2}
                        onClick={() => onItemSelect?.(item)}
                      >
                        {item.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {item.createdAt.toLocaleDateString()}
                      </Text>
                    </Stack>
                    <Menu shadow="md">
                      <Menu.Target>
                        <ActionIcon size="xs" variant="subtle">
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => setEditingItem(item)}
                        >
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={() => onItemDuplicate?.(item)}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  {item.properties.length > 0 && (
                    <Group gap="xs" mt="xs">
                      {item.properties.slice(0, 2).map(prop => (
                        <Badge key={prop.propertyId} size="sm" variant="light">
                          {String(prop.value).slice(0, 12)}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </div>
              </Card>
            ))}
          </SimpleGrid>
        </div>
      </ScrollArea>

      <Modal
        opened={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        title="Add Item"
        size="sm"
      >
        <Stack>
          <TextInput
            placeholder="Item title"
            label="Title"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateItem();
            }}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setIsAddingItem(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateItem} disabled={!newItemTitle.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Edit Item"
        size="sm"
      >
        {editingItem && (
          <Stack>
            <TextInput
              label="Title"
              value={editingItem.title}
              onChange={(e) =>
                setEditingItem({ ...editingItem, title: e.currentTarget.value })
              }
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (editingItem) {
                    onItemUpdate?.(editingItem);
                    setEditingItem(null);
                  }
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default GalleryView;
